import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import entityComposition from '../common/classes/entityComposition';
import FluidIssueEditor from './fluidIssueEditor';
import { viewPermissionSelector, editPermissionSelector } from '../common/classes/permissions';
import routeHelper from '../common/classes/routeHelper';
import SaveButton from '../common/controls/saveButton';
const FluidIssueEditorContainer = ({
  canView,
  match,
  location,
  issue: _issue = {},
  issueActions: {
    serverSave,
    serverShow
  } = {},
  canEdit,
  match: {
    params: {
      id
    }
  }
}) => {
  const {
    active = {
      isNew: false
    },
    loading,
    pendingChanges
  } = _issue;

  // Redirect and post authorization error message if permissions insufficient
  useEffect(() => {
    routeHelper.handleViewRedirect(canView, location);
  }, [canView, location]);

  // define an instance variable so that useCallback methods have the latest data
  const activeRef = useRef();
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  // load data when component loads
  useEffect(() => {
    const {
      current
    } = activeRef;
    if (id !== current.id && !current.isNew) {
      // load job by id into redux state for legacy components to consume
      serverShow(id);
    }
  }, [activeRef, id, serverShow]);

  // useCallback prevents recreating this method, which is passed to children and will cause
  // re-rendering. To get the latest active record use a ref as recommended in the
  // docs: https://reactjs.org/docs/hooks-faq.html#how-to-read-an-often-changing-value-from-usecallback
  const handleSave = useCallback(() => {
    const version = active && active.content && active.content.version || undefined;
    serverSave(version);
  }, [serverSave, active]);
  let saveState = SaveButton.STATUS_DISABLED;
  if (canEdit) {
    if (pendingChanges && !loading) {
      saveState = SaveButton.STATUS_PENDING;
    } else if (pendingChanges && loading) {
      saveState = SaveButton.STATUS_IN_PROGRESS;
    }
  }
  return /*#__PURE__*/React.createElement(FluidIssueEditor, {
    issueID: id,
    sidebar: true,
    handleSave: handleSave,
    saveState: saveState,
    match: match,
    footer: null,
    width: "md"
  });
};
FluidIssueEditorContainer.propTypes = {
  canView: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  issue: PropTypes.shape({
    active: PropTypes.shape({
      content: PropTypes.shape({
        version: PropTypes.string
      }),
      isNew: PropTypes.bool,
      id: PropTypes.string,
      assets: PropTypes.arrayOf(PropTypes.shape({})),
      jobNumber: PropTypes.string,
      documents: PropTypes.arrayOf(PropTypes.shape({})),
      phaseCodes: PropTypes.arrayOf(PropTypes.shape({})),
      piecesOther: PropTypes.arrayOf(PropTypes.shape({})),
      flangeValveInspection: PropTypes.arrayOf(PropTypes.shape({})),
      flangeValve: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    loading: PropTypes.bool.isRequired,
    pendingChanges: PropTypes.bool.isRequired
  }),
  issueActions: PropTypes.shape({
    serverSave: PropTypes.func,
    serverShow: PropTypes.func,
    showCancel: PropTypes.func
  }),
  location: PropTypes.shape({}).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      formID: PropTypes.string
    }),
    url: PropTypes.string
  }).isRequired
};
export { FluidIssueEditorContainer as TestFluidIssueEditorContainer };
export default entityComposition(FluidIssueEditorContainer, ['issue'], {
  canEdit: state => editPermissionSelector('issue')(state),
  canView: state => viewPermissionSelector('issue')(state)
});